import { useCallback, useState } from 'react'

import { removeFromStorage } from 'src/shared/helpers/sessionStorageHelpers'
import { UserActions, useUserDispatch } from 'src/state/userState'

import { InvalidAccessTokenError, ServerErrors } from './constants'
import { ErrorMessage } from './types'

import axios from 'axios'

export const defaultCallApi = async <T, S>(apiRequest: (props: T) => Promise<S>, props: T) => {
    try {
        return await apiRequest(props)
    } catch (err) {
        if (axios.isCancel(err)) {
            return
        }

        throw err
    }
}

const useAPIRequest = <T, S>(apiRequest: (props: T) => Promise<S>) => {
    const userDispatch = useUserDispatch()

    const [error, setError] = useState<boolean>(false)
    const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>(null)
    const [result, setResult] = useState<S>(undefined)
    const [loading, setLoading] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>(false)

    const reset = useCallback(() => {
        setError(false)
        setErrorMessages(null)
        setResult(undefined)
        setLoading(false)
        setSuccess(false)
    }, [])

    const callAPI = useCallback(
        async (props: T = {} as T) => {
            setLoading(true)
            setError(false)
            setErrorMessages(null)
            setSuccess(false)

            try {
                const data = await apiRequest(props)
                setResult(data as S)
                setSuccess(true)
                return data
            } catch (err) {
                if (axios.isCancel(err)) {
                    return
                }

                if (err instanceof InvalidAccessTokenError) {
                    removeFromStorage('user')
                    userDispatch({ type: UserActions.LOG_OUT })
                }

                if (err instanceof ServerErrors) {
                    setErrorMessages(err.errors)
                }

                setError(true)
                throw err
            } finally {
                setLoading(false)
            }
        },
        [apiRequest, userDispatch],
    )

    return { callAPI, result, error, errorMessages, success, loading, reset }
}

export default useAPIRequest
